<template>
  <div class="workspace-app-creating__root">
    <div class="workspace-app-creating__circle">
      <el-progress class="creating-app" type="circle" :percentage="pctVal" :width="48" :stroke-width="5">
        <div class="workspace-app-creating__pct">{{ pctVal }}%</div>
      </el-progress>
    </div>
    <div class="workspace-app-creating__label-1">正在创建应用</div>
    <div class="workspace-app-creating__label-2">应用支持二次编辑，定制化满足您的需求...</div>
  </div>
</template>

<script lang="ts" setup>
import {
  apiPostAppCreate,
  apiPostAppCreateBy_tpl,
  ReqAppCreate,
  ReqAppCreateByTpl,
  ReqMiddlePageAppPreCheck,
} from '@vas2t-lowcode/api';
import { computed, nextTick, onMounted, ref } from 'vue';

const props = defineProps({
  /** 平台id */
  platformId: {
    type: String,
    default: '',
    require: true,
  },
  /** 模板id */
  tplId: {
    type: String,
    default: '',
    require: true,
  },
  /**
   * 工作区id
   */
  workspaceId: {
    type: Number,
    default: 0,
    require: true,
  },
});

/** 循环百分比 */
const pctVal = ref(1);

/** 终止循环的标记 */
const pauseFlag = ref(false);

/** 启动循环 */
const loadingStart = () => {
  const start = () => {
    const val = pctVal.value;
    window.requestAnimationFrame(() => {
      pctVal.value = val + 1;

      if (pctVal.value < 96 && !pauseFlag.value) {
        start();
      }
    });
  };

  start();
};

/** 终止loading */
const loadingEnd = () => {
  pauseFlag.value = true;
  return nextTick(() => {
    pctVal.value = 100;
  });
};

/** 是否成功创建应用 */
const appData = ref<ReqMiddlePageAppPreCheck>();
/** app创建中状态值 */
const isCreateAppLoading = ref(false);
/** 创建应用 */
const createAppHandler = () => {
  // 创建应用
  if (isCreateAppLoading.value) {
    return;
  }
  // loading 动画开始
  loadingStart();

  isCreateAppLoading.value = true;
  const { platformId, workspaceId, tplId } = props;
  const params = {
    platform_id: platformId,
    team_id: workspaceId,
    tpl_id: tplId,
  } as ReqAppCreateByTpl;

  return apiPostAppCreateBy_tpl(params, { timeout: 15000 })
    .then(({ data }) => {
      // 打开新tab页
      appData.value = {
        platform_id: params.platform_id,
        team_id: data.team_id,
        third_app_id: data.third_app_id,
        third_ws_id: data.ws_id,
        third_app_name: data.third_app_name,
      } as ReqMiddlePageAppPreCheck;
    })
    .finally(() => {
      // loading 动画结束
      loadingEnd();
      isCreateAppLoading.value = false;

      // 通知外部应用创建结果
      emit('on-workspace-app-created', appData.value);
    });
};

/** 是否创建空白应用 */
const isCreatingBlankApp = computed(() => {
  return !props.tplId;
});

const emit = defineEmits<{
  // 创建失败则不返回应用数据
  (e: 'on-workspace-app-created', data?: ReqMiddlePageAppPreCheck): void;
}>();

/**
 * 创建空白应用
 */
const createBlankAppHandler = () => {
  // 创建应用
  if (isCreateAppLoading.value) {
    return;
  }
  // loading 动画开始
  loadingStart();

  isCreateAppLoading.value = true;
  const { platformId, workspaceId } = props;
  const params = {
    platform_id: platformId,
    team_id: workspaceId,
  } as ReqAppCreate;

  return apiPostAppCreate(params, { timeout: 10000 })
    .then(({ data }) => {
      // 打开新tab页
      appData.value = {
        platform_id: params.platform_id,
        team_id: data.team_id,
        third_app_id: data.third_app_id,
        third_ws_id: data.ws_id,
        third_app_name: data.third_app_name,
      } as ReqMiddlePageAppPreCheck;
    })
    .finally(() => {
      // loading 动画结束
      loadingEnd();
      isCreateAppLoading.value = false;

      // 通知外部应用创建结果
      emit('on-workspace-app-created', appData.value);
    });
};

onMounted(() => {
  if (!isCreatingBlankApp.value) {
    // 创建模板应用
    createAppHandler();
    return;
  }

  // 创建空白应用
  createBlankAppHandler();
});
</script>

<style lang="scss" scoped>
.workspace-app-creating {
  &__root {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__pct {
    color: var(--lowcode-theme-common-01);
    height: 14px;
    font-size: 12px;
    font-weight: bold;
    line-height: 14px;
    text-align: center;
  }

  &__label-1 {
    margin-top: 12px;
    height: 28px;
    font-size: 16px;
    color: var(--lowcode-text-01);
    line-height: 28px;
  }

  &__label-2 {
    margin-top: 2px;
    height: 22px;
    font-size: 14px;
    color: var(--lowcode-text-03);
    line-height: 22px;
  }
}
</style>

<style lang="scss">
.creating-app {
  .el-progress-circle__path {
    stroke: var(--lowcode-theme-common-01) !important;
  }
}
</style>
